import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import type { AvailableSecret } from '@reflex/shared';

export type { AvailableSecret };

/**
 * Picker-side owner context. Maps 1:1 to the server's
 * `ListAvailableScope`. `user` and `team` carry the target id; `organization`
 * is just a marker (the active org comes from the auth context).
 */
export type AvailableSecretsOwner =
  | { ownerType: 'user'; ownerUserId: string }
  | { ownerType: 'team'; ownerTeamId: string }
  | { ownerType: 'organization' };

interface UseAvailableSecretsResult {
  secrets: AvailableSecret[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Stable string signature for an owner so the React Query cache key
 * doesn't have to embed the whole object. Two equal contexts must
 * produce the same signature; missing fields contribute an empty
 * segment so two distinct keys can never share the same signature.
 */
function ownerSignature(owner?: AvailableSecretsOwner): string {
  if (!owner) return '';
  if (owner.ownerType === 'user') return `user:${owner.ownerUserId}`;
  if (owner.ownerType === 'team') return `team:${owner.ownerTeamId}`;
  return 'organization';
}

/**
 * Fetches secrets that can be attached to an agent env var. Supports
 * server-side filtering by name (`nameQuery`) and owner-context scoping
 * (`owner`).
 *
 * - Launch dialog and user-owned persona editor: pass
 *   `{ ownerType: 'user', ownerUserId: currentUser.id }` (or omit and
 *   let the server default fire).
 * - Team-owned persona editor: pass
 *   `{ ownerType: 'team', ownerTeamId }`.
 * - Org-owned persona editor: pass `{ ownerType: 'organization' }`.
 *
 * Callers typically debounce user input before passing it here — the query
 * re-runs whenever the normalized query changes. Previous results are kept
 * visible during refetches to avoid a UI flash.
 */
export function useAvailableSecrets(
  nameQuery?: string,
  owner?: AvailableSecretsOwner,
): UseAvailableSecretsResult {
  const normalized = (nameQuery ?? '').trim();
  const orgKey = useOrgKey();
  const sig = ownerSignature(owner);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.secretsAvailable(orgKey, normalized, sig),
    queryFn: () =>
      api.listAvailableSecrets(
        normalized || undefined,
        owner
          ? {
              ownerType: owner.ownerType,
              ownerUserId: 'ownerUserId' in owner ? owner.ownerUserId : undefined,
              ownerTeamId: 'ownerTeamId' in owner ? owner.ownerTeamId : undefined,
            }
          : undefined,
      ),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  return {
    secrets: data?.secrets ?? [],
    isLoading,
    isError,
  };
}
